import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { tenantsService } from '../../api/tenantsService';
import type { Tenant } from '../../api/tenantsService';

export function TenantsListPage() {
  const navigate = useNavigate();

  const listPage = useAdminListPage<Tenant, string>({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantsService.list(),
    deleteFn: (id) => tenantsService.remove(id),
    getDeleteId: (tenant) => tenant.slug,
  });

  return (
    <AdminListPage<Tenant>
      title="Tenants"
      subtitle="Manage your application tenants"
      loadingLabel="Loading tenants..."
      errorTitle="Failed to load tenants"
      errorFallback="An unexpected error occurred while loading tenants."
      emptyTitle="No tenants found"
      emptyDescription="Get started by creating your first tenant."
      createLabel="Create Tenant"
      onCreateClick={() => navigate('/admin/tenants/create')}
      items={listPage.items}
      getRowKey={(tenant) => tenant.id}
      onRowClick={(tenant) => navigate(`/admin/tenants/${tenant.slug}`)}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Tenant"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(tenant) => tenant.name}
      deleteNoun="tenant"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      renderHead={() => (
        <>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Slug</Table.HeaderCell>
          <Table.HeaderCell>Domain</Table.HeaderCell>
          <Table.HeaderCell align="center">Users</Table.HeaderCell>
          <Table.HeaderCell align="center">Status</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(tenant) => (
        <>
          <Table.Cell>
            <Text weight="medium">{tenant.name}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {tenant.slug}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {tenant.domain ?? '--'}
            </Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Text size="sm">{tenant.user_count}</Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={tenant.is_active ? 'success' : 'default'}
              size="sm"
            >
              {tenant.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${tenant.name}`}
                leftIcon={<Icon icon={Pencil} size="sm" />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/tenants/${tenant.slug}/edit`);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${tenant.name}`}
                leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                onClick={(e) => {
                  e.stopPropagation();
                  listPage.setDeleteTarget(tenant);
                }}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
